import React from 'react';
import ValidationElement from 'components/Form/ValidationElement';
import Show from 'components/Form/Show';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import Branch from 'components/Form/Branch';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import DateRange from 'components/Form/DateRange';
import Textarea from 'components/Form/Textarea';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import { PsychologicalDiagnosisItemModel } from 'forms/sections/psychological/diagnoses/types';
import {
  CommonAddressBooks,
  CommonOnError,
  CommonOnUpdate,
  CommonScrollIntoView,
} from 'types/props';
import { Dispatch } from 'redux';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import Treatment from '../Treatment';

interface Props extends ValidationElementProps, Partial<PsychologicalDiagnosisItemModel> {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  prefix?: string
  required?: boolean
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
  attribute?: string
  formType?: string
}

export default class Diagnosis extends ValidationElement<Props> {
  update = (queue: Partial<PsychologicalDiagnosisItemModel>) => {
    this.props.onUpdate({
      Condition: this.props.Condition,
      Diagnosed: this.props.Diagnosed,
      Treatment: this.props.Treatment,
      SameTreatmentFacility: this.props.SameTreatmentFacility,
      TreatmentFacility: this.props.TreatmentFacility,
      Effective: this.props.Effective,
      Explanation: this.props.Explanation,
      Comments: this.props.Comments,
      ...queue,
    });
  };

  updateCondition = (values: PsychologicalDiagnosisItemModel['Condition']) => {
    this.update({
      Condition: values,
    });
  };

  updateDiagnosed = (values: PsychologicalDiagnosisItemModel['Diagnosed']) => {
    this.update({
      Diagnosed: values,
    });
  };

  updateTreatment = (values: PsychologicalDiagnosisItemModel['Treatment']) => {
    const additional: Partial<PsychologicalDiagnosisItemModel> = { };
    if (this.props.SameTreatmentFacility?.value === 'Yes') {
      additional.TreatmentFacility = values;
    }
    this.update({
      Treatment: values,
      ...additional
    });
  };

  updateSameTreatmentFacility = (values: PsychologicalDiagnosisItemModel['SameTreatmentFacility']) => {
    const additional: Partial<PsychologicalDiagnosisItemModel> = {};
    if (values?.value === 'Yes') {
      additional.TreatmentFacility = this.props.Treatment;
    }
    this.update({
      SameTreatmentFacility: values,
      ...additional
    });
  };

  updateTreatmentFacility = (values: PsychologicalDiagnosisItemModel['TreatmentFacility']) => {
    this.update({
      TreatmentFacility: values,
    });
  };

  updateEffective = (values: PsychologicalDiagnosisItemModel['Effective']) => {
    this.update({
      Effective: values,
    });
  };

  updateExplanation = (values: PsychologicalDiagnosisItemModel['Explanation']) => {
    this.update({
      Explanation: values,
    });
  };

  updateComments = (values: PsychologicalDiagnosisItemModel['Comments']) => {
    this.update({
      Comments: values,
    });
  };

  render() {
    const prefix = this.props.prefix;
    const attribute = `${this.props.attribute}.items[${this.props.$index}].Item`;

    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="diagnosis">
        <Field
          title={i18n.t(`psychological.pvq.${prefix}.heading.diagnosed`)}
          attribute={`${attribute}.Diagnosed`}
          help={`psychological.${prefix}.help.diagnosed`}
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            name="Diagnosed"
            attribute={`${attribute}.Diagnosed`}
            {...this.props.Diagnosed}
            onUpdate={this.updateDiagnosed}
            minDateEqualTo
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Field
          title={i18n.t(`psychological.pvq.${prefix}.heading.healthcareProfessional`)}
          titleSize="h4"
          optional
          className="no-margin-bottom"
        />
        <div className="person">
          <Treatment
            {...this.props.Treatment}
            name="Treatment"
            attribute={`${attribute}.Treatment`}
            prefix={`${prefix}.person`}
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            onUpdate={this.updateTreatment}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            formType={this.props.formType}
            showFullName
          />
        </div>
        {prefix !== 'existingConditions.diagnosis' && (
          <>
            <Branch
              attribute={`${attribute}.SameTreatmentFacility`}
              name="SameTreatmentFacility"
              label={i18n.t(`psychological.pvq.${prefix}.heading.sameTreatmentFacility`)}
              labelSize="h4"
              className="eapp-field-wrap hascondition"
              {...this.props.SameTreatmentFacility}
              warning
              onError={this.handleError}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              onUpdate={this.updateSameTreatmentFacility}
            />
            {this.props.SameTreatmentFacility?.value === 'No' && (
              <>
                <Field
                  title={i18n.t(`psychological.pvq.${prefix}.heading.facility`)}
                  attribute={`${attribute}.Facility`}
                  titleSize="h4"
                  optional
                  className="no-margin-bottom"
                />
                <div className="facility">
                  <Treatment
                    {...this.props.TreatmentFacility}
                    name="TreatmentFacility"
                    attribute={`${attribute}.TreatmentFacility`}
                    prefix={`${prefix}.facility`}
                    addressBooks={this.props.addressBooks}
                    dispatch={this.props.dispatch}
                    onUpdate={this.updateTreatmentFacility}
                    onError={this.props.onError}
                    required={this.props.required}
                    scrollIntoView={this.props.scrollIntoView}
                    formType={this.props.formType}
                  />
                </div>
              </>
            )}
          </>
        )}
        {/*
        <Show when={this.props.prefix !== 'existingConditions.diagnosis'}>
          <div>
            <Field
              title={i18n.t(`psychological.pvq.${prefix}.heading.effective`)}
              attribute={`${attribute}.Effective`}
              className="no-margin-bottom"
              adjustFor="buttons"
              scrollIntoView={this.props.scrollIntoView}
            >
              <RadioGroup
                className="effective option-list"
                attribute={`${attribute}.Effective`}
                selectedValue={(this.props.Effective || {}).value}
                onError={this.props.onError}
                required={this.props.required}
              >
                <Radio
                  name="effective"
                  label="Yes"
                  value="Yes"
                  className="yes"
                  onUpdate={this.updateEffective}
                  onError={this.props.onError}
                />
                <Radio
                  name="effective"
                  label="No"
                  value="No"
                  className="No"
                  onUpdate={this.updateEffective}
                  onError={this.props.onError}
                />
              </RadioGroup>
            </Field>

            <Show when={(this.props.Effective || {}).value === 'No'}>
              <Field
                title={i18n.t(`psychological.pvq.${prefix}.heading.explanation`)}
                attribute={`${attribute}.Explanation`}
                titleSize="label"
                help={`psychological.pvq.${prefix}.help.explanation`}
                scrollIntoView={this.props.scrollIntoView}
              >
                <Textarea
                  name="Explanation"
                  attribute={`${attribute}.Explanation`}
                  className="explanation"
                  {...this.props.Explanation}
                  onUpdate={this.updateExplanation}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>
            </Show>
          </div>
        </Show>
        */}
      </div>
    ) : (
      <div className="diagnosis">
        <Show when={this.props.prefix !== 'existingConditions.diagnosis'}>
          <Field
            title={i18n.t(`psychological.diagnosis.heading.condition`)}
            attribute={`${attribute}.Condition`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <RadioGroup
              className="diagnosis-condition option-list option-list-vertical"
              attribute={`${attribute}.Condition`}
              onError={this.props.onError}
              required={this.props.required}
              selectedValue={(this.props.Condition || {}).value}
            >
              <Radio
                name="diagnosis-condition-psychotic"
                label={i18n.t('psychological.diagnosis.label.psychotic')}
                value="Psychotic disorder"
                className="diagnosis-condition-psychotic"
                onUpdate={this.updateCondition}
                onError={this.props.onError}
              />
              <Radio
                name="diagnosis-condition-schizophrenia"
                label={i18n.t('psychological.diagnosis.label.schizophrenia')}
                value="Schizophrenia"
                className="diagnosis-condition-schizophrenia"
                onUpdate={this.updateCondition}
                onError={this.props.onError}
              />
              <Radio
                name="diagnosis-condition-schizoaffective"
                label={i18n.t('psychological.diagnosis.label.schizoaffective')}
                value="Schizoaffective disorder"
                className="diagnosis-condition-schizoaffective"
                onUpdate={this.updateCondition}
                onError={this.props.onError}
              />
              <Radio
                name="diagnosis-condition-delusional"
                label={i18n.t('psychological.diagnosis.label.delusional')}
                value="Delusional disorder"
                className="diagnosis-condition-delusional"
                onUpdate={this.updateCondition}
                onError={this.props.onError}
              />
              <Radio
                name="diagnosis-condition-bipolar"
                label={i18n.t('psychological.diagnosis.label.bipolar')}
                value="Bipolar mood disorder"
                className="diagnosis-condition-bipolar"
                onUpdate={this.updateCondition}
                onError={this.props.onError}
              />
              <Radio
                name="diagnosis-condition-borderline"
                label={i18n.t('psychological.diagnosis.label.borderline')}
                value="Borderline personality disorder"
                className="diagnosis-condition-borderline"
                onUpdate={this.updateCondition}
                onError={this.props.onError}
              />
              <Radio
                name="diagnosis-condition-antisocial"
                label={i18n.t('psychological.diagnosis.label.antisocial')}
                value="Antisocial personality disorder"
                className="diagnosis-condition-antisocial"
                onUpdate={this.updateCondition}
                onError={this.props.onError}
              />
            </RadioGroup>
          </Field>
        </Show>

        <Field
          title={i18n.t(`psychological.${prefix}.heading.diagnosed`)}
          attribute={`${attribute}.Diagnosed`}
          help={`psychological.${prefix}.help.diagnosed`}
          adjustFor="daterange"
          scrollIntoView={this.props.scrollIntoView}
        >
          <DateRange
            name="Diagnosed"
            attribute={`${attribute}.Diagnosed`}
            {...this.props.Diagnosed}
            onUpdate={this.updateDiagnosed}
            minDateEqualTo
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
          />
        </Field>

        <Field
          title={i18n.t(`psychological.${prefix}.heading.healthcareProfessional`)}
          titleSize="h4"
          optional
          className="no-margin-bottom"
        />
        <div className="person">
          <Treatment
            name="Treatment"
            attribute={`${attribute}.Treatment`}
            {...this.props.Treatment}
            prefix={`${prefix}.person`}
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            onUpdate={this.updateTreatment}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          />
        </div>

        <Field
          title={i18n.t(`psychological.${prefix}.heading.facility`)}
          attribute={`${attribute}.Facility`}
          titleSize="h4"
          optional
          className="no-margin-bottom"
        />
        <div className="facility">
          <Treatment
            name="TreatmentFacility"
            attribute={`${attribute}.TreatmentFacility`}
            {...this.props.TreatmentFacility}
            prefix={`${prefix}.facility`}
            addressBooks={this.props.addressBooks}
            dispatch={this.props.dispatch}
            onUpdate={this.updateTreatmentFacility}
            onError={this.props.onError}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            formType={this.props.formType}
          />
        </div>

        <Show when={this.props.prefix !== 'existingConditions.diagnosis'}>
          <div>
            <Field
              title={i18n.t(`psychological.${prefix}.heading.effective`)}
              attribute={`${attribute}.Effective`}
              className="no-margin-bottom"
              adjustFor="buttons"
              scrollIntoView={this.props.scrollIntoView}
            >
              <RadioGroup
                className="effective option-list"
                attribute={`${attribute}.Effective`}
                selectedValue={(this.props.Effective || {}).value}
                onError={this.props.onError}
                required={this.props.required}
              >
                <Radio
                  name="effective"
                  label="Yes"
                  value="Yes"
                  className="yes"
                  onUpdate={this.updateEffective}
                  onError={this.props.onError}
                />
                <Radio
                  name="effective"
                  label="No"
                  value="No"
                  className="No"
                  onUpdate={this.updateEffective}
                  onError={this.props.onError}
                />
              </RadioGroup>
            </Field>

            <Show when={(this.props.Effective || {}).value === 'No'}>
              <Field
                title={i18n.t(`psychological.${prefix}.heading.explanation`)}
                attribute={`${attribute}.Explanation`}
                titleSize="label"
                help={`psychological.${prefix}.help.explanation`}
                scrollIntoView={this.props.scrollIntoView}
              >
                <Textarea
                  name="Explanation"
                  attribute={`${attribute}.Explanation`}
                  className="explanation"
                  {...this.props.Explanation}
                  onUpdate={this.updateExplanation}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>
            </Show>
          </div>
        </Show>
        <Comments
          title={i18n.t(`psychological.${prefix}.comments`)}
          name="DiagnosisComments"
          className="optional-comments"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
      </div>
    );
  }
}

Diagnosis.defaultProps = {
  prefix: 'diagnosis',
  addressBooks: {},
  Comments: {},
  SameTreatmentFacility: {}, // PVQ Only
  formType: '',
  dispatch: (action) => { },
  onUpdate: (queue) => { },
  onError: (value, arr: ErrorCode[]) => arr,
};
